<script>
  /**
   * IELTS aggregated report for an invitation: the four skill bands the
   * candidate achieved, the averaged overall band, and how that band compares
   * with the other tests. The IELTS counterpart of the TOEIC consolidated
   * report on the same page, built from the same already-loaded
   * `definitions-detail` rows so it costs no extra requests.
   *
   * All prose is static text chosen by band tier — the report never generates
   * feedback per candidate. The long-form wording lives in the per-skill
   * detailed reports, which each column links to.
   *
   * Mirrors AssessmentInvitationIeltsReport.svelte in sensay-assessment-admin.
   */
  import { _ } from "svelte-i18n";
  import { createEventDispatcher } from "svelte";
  import { currentPage, invitationNumber } from "../../utilities/DataStore";
  import { formatDateToLocale } from "../../utilities/FormatUtils.svelte";
  import {
    formatIeltsBand,
    ieltsBandNameIndex,
    ieltsReadingBandLevel,
  } from "../ieltsReading.mjs";
  import {
    IELTS_COMPARISON_ROWS,
    buildIeltsOverallReport,
    ieltsComparisonRowIndex,
  } from "../ieltsOverall.mjs";

  export let invitationDetail = {};
  export let assessmentDefinitionDetail = [];

  const dispatch = createEventDispatcher();

  $: report = buildIeltsOverallReport(assessmentDefinitionDetail);

  // ieltsReadingBandLevel is the shared 1-5 descriptor tier: the band
  // thresholds behind the tiers are the same for all four skills and for the
  // overall band, which is why the reading helper is reused unchanged.
  $: bandLevel = ieltsReadingBandLevel(report.band);
  $: bandNameIndex = ieltsBandNameIndex(report.band);
  $: comparisonRow = ieltsComparisonRowIndex(report.band);

  // The invitation's own timestamps are the fallback: they describe when the
  // invitation was created and closed, which is only the same thing as the
  // candidate's sitting when no skill row carries its own dates.
  $: startedAt = report.startedAt || invitationDetail?.createdAt;
  $: finishedAt = report.finishedAt || invitationDetail?.finishedAt;

  /**
   * The link a reader copies off the report. It lives here rather than on the
   * per-skill reports because this is the page it opens: the overall band with
   * all four papers behind it. The store is the fallback for the report the
   * reader reached from an /invitation-report URL before the detail loaded.
   *
   * The host is the page's own, so a link copied from a preview deploy or a
   * white-label domain keeps the reader on the site they are already on.
   */
  const reportOrigin = window.location.origin;
  const reportHost = window.location.host;

  $: reportInvitation = invitationDetail?.invitationNumber || $invitationNumber;
  $: reportPath = `/#/invitation-report/${reportInvitation}`;
</script>

<div class="container ielts-report paper-shadow">
  <div class="row justify-content-center">
    <div class="col text-center">
      <img
        src="assets/img/logo_horizontal_nobg2.png"
        width="200px"
        alt="logo"
      />
    </div>
  </div>

  <div class="row mt-3">
    <div class="col text-center">
      <h1 class="report-title">{$_("IELTS_OVERALL_REPORT_TITLE")}</h1>
    </div>
  </div>

  <hr class="mb-4" />

  {#if !report.hasSkills}
    <p class="text-muted mb-0">{$_("IELTS_OVERALL_NO_SKILLS")}</p>
  {:else}
    <!-- Candidate on the left, overall band in a fixed-width card on the
         right, so the band sits in the same place on every report. -->
    <div class="candidate-row ielts-avoid-break">
      <div class="candidate-details">
        <h2 class="candidate-name">{invitationDetail?.clientName || "-"}</h2>
        <p class="text-muted mb-3">{invitationDetail?.clientId || ""}</p>

        <div class="meta-line">
          <span class="meta-label">{$_("TIME_STARTED")}:</span>
          {formatDateToLocale(startedAt) || "-"}
        </div>
        <div class="meta-line">
          <span class="meta-label">{$_("TIME_COMPLETED")}:</span>
          {formatDateToLocale(finishedAt) || "-"}
        </div>
        <div class="meta-line">
          <span class="meta-label">{$_("INVITATION_NUMBER")}:</span>
          {invitationDetail?.invitationNumber || "-"}
        </div>

        {#if reportInvitation}
          <div class="mt-2">
            <i class="fas fa-lock"
              ><a
                class="ml-2 sensayLink"
                style="font-family: 'Noto Sans', sans-serif;"
                href={reportOrigin + reportPath}>{reportHost}{reportPath}</a
              ></i
            >
          </div>
        {/if}
      </div>

      <div class="band-card">
        <div class="band-label">{$_("IELTS_OVERALL_BAND")}</div>
        <div class="band-score">{formatIeltsBand(report.band)}</div>
        {#if bandNameIndex !== null}
          <div class="band-name">{$_("IELTS_BAND_NAME_" + bandNameIndex)}</div>
        {/if}
      </div>
    </div>

    <div class="feedback ielts-avoid-break">
      {#if report.band === null}
        <p class="feedback-text">{$_("IELTS_OVERALL_NO_BAND")}</p>
      {:else}
        {#if report.scoredCount < 4}
          <p class="partial-note">{$_("IELTS_OVERALL_PARTIAL_BAND")}</p>
        {/if}

        <div class="feedback-label">{$_("IELTS_BAND_DESCRIPTOR")}</div>
        <p class="feedback-text">
          {$_("IELTS_BAND_DESCRIPTOR_" + bandNameIndex)}
        </p>

        {#if bandLevel !== null}
          <div class="feedback-label">{$_("IELTS_WHAT_THIS_MEANS")}</div>
          <p class="feedback-text">{$_("IELTS_OVERALL_LEVEL" + bandLevel)}</p>
        {/if}

        <div class="feedback-label">{$_("IELTS_HOW_THIS_IS_ASSESSED")}</div>
        <p class="feedback-text">
          {$_("IELTS_OVERALL_CRITERIA")}
          {$_("IELTS_BAND_SCALE_NOTE")}
        </p>
      {/if}
    </div>

    <div class="row skill-row mt-4">
      {#each report.skills as entry (entry.skill)}
        <div class="col-lg-3 col-md-6 mb-4">
          <div class="skill-card ielts-avoid-break">
            <div class="skill-header">{$_(entry.skill)}</div>

            <div class="skill-body">
              <div class="skill-band">{formatIeltsBand(entry.band)}</div>
              {#if entry.band !== null}
                <div class="skill-band-name">
                  {$_("IELTS_BAND_NAME_" + ieltsBandNameIndex(entry.band))}
                </div>
              {:else}
                <div class="skill-band-name text-muted">
                  {entry.started
                    ? $_("IELTS_SKILL_NOT_SCORED")
                    : $_("TEST_NOT_STARTED")}
                </div>
              {/if}

              {#if ieltsReadingBandLevel(entry.band) !== null}
                <p class="skill-text">
                  {$_(
                    "IELTS_" +
                      entry.skill +
                      "_SUMMARY" +
                      ieltsReadingBandLevel(entry.band),
                  )}
                </p>

                <div class="skill-label">{$_("IELTS_SKILL_NEXT_STEPS")}</div>
                <p class="skill-text">
                  {$_(
                    "IELTS_" +
                      entry.skill +
                      "_NEXT_STEPS" +
                      ieltsReadingBandLevel(entry.band),
                  )}
                </p>
              {/if}
            </div>

            <!-- The per-skill report is the authority on how a band was
                 awarded, so every paper the candidate actually sat links to
                 its own report. Screen only: in print the reader has the
                 whole document already. -->
            <div class="skill-footer no-print">
              {#if entry.instanceId}
                <button
                  type="button"
                  class="btn btn-primary btn-sm"
                  on:click={() =>
                    dispatch("openReport", {
                      instanceId: entry.instanceId,
                      definitionId: entry.definitionId,
                      skill: entry.skill,
                    })}>{$_("DETAILED_REPORT")}</button
                >
              {/if}
            </div>
          </div>
        </div>
      {/each}
    </div>

    <div class="row mt-4">
      <div class="col">
        <h4 class="section-title">{$_("IELTS_COMPARISON_TABLE")}</h4>
        <p class="text-muted small">{$_("IELTS_COMPARISON_DESCRIPTION")}</p>

        <div class="table-responsive">
          <table class="table table-bordered comparison-table">
            <thead>
              <tr>
                <th>{$_("IELTS_COMPARISON_BAND")}</th>
                <th>{$_("CEFR")}</th>
                <th>{$_("IELTS_COMPARISON_TOEIC")}</th>
                <th>{$_("IELTS_COMPARISON_TOEIC_SW")}</th>
                <th>{$_("TOEFL")}</th>
              </tr>
            </thead>
            <tbody>
              {#each IELTS_COMPARISON_ROWS as row, index (row.cefr)}
                <tr class:table-primary={index === comparisonRow}>
                  <th>{row.band}</th>
                  <td>{row.cefr}</td>
                  <td>{row.toeic}</td>
                  <td>{row.toeicSw}</td>
                  <td>{row.toefl}</td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  {/if}

  <div class="row justify-content-center mt-4 report-footer-link">
    <a
      href="/"
      on:click={() => {
        $currentPage = "";
      }}>assessment.oksensay.com</a
    >
  </div>
</div>

<style>
  /* Palette and type scale match the per-skill IELTS reports so the overall
     report and the reports it links to read as one document. */
  .report-title {
    font-size: 1.9rem;
    font-weight: 700;
    color: #1f2937;
  }

  .candidate-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1.5rem;
  }

  .candidate-details {
    min-width: 0;
  }

  .candidate-name {
    font-weight: 700;
    color: #1f2937;
    margin-bottom: 0.25rem;
  }

  .meta-line {
    color: #4b5563;
    line-height: 1.9;
  }

  .meta-label {
    font-weight: 700;
    color: #374151;
  }

  .band-card {
    flex: 0 0 14rem;
    width: 14rem;
    text-align: center;
    border: 2px solid #0056b3;
    border-radius: 0.5rem;
    padding: 0.75rem 1rem;
  }

  .band-label {
    font-size: 0.8rem;
    font-weight: 700;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: #6b7280;
  }

  .band-score {
    font-size: 5rem;
    font-weight: 700;
    line-height: 1.05;
    color: #0056b3;
  }

  .band-name {
    font-size: 0.9rem;
    font-weight: 600;
    color: #4b5563;
  }

  .feedback {
    margin-top: 1.5rem;
  }

  .feedback-label {
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-size: 0.7rem;
    color: #6b7280;
    margin-top: 1rem;
  }

  .feedback-text {
    color: #4b5563;
    line-height: 1.7;
    margin-bottom: 0;
  }

  .partial-note {
    font-size: 0.85rem;
    color: #92400e;
    background: #fffbeb;
    border: 1px solid #fde68a;
    border-radius: 0.375rem;
    padding: 0.5rem 0.75rem;
  }

  .section-title {
    font-weight: 700;
    color: #1f2937;
  }

  /* Equal-height skill cards: the four columns carry different amounts of
     prose, and a ragged bottom edge would make the row hard to scan. */
  .skill-card {
    display: flex;
    flex-direction: column;
    height: 100%;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    overflow: hidden;
  }

  .skill-header {
    background: #f9fafb;
    border-bottom: 1px solid #e5e7eb;
    padding: 0.5rem 1rem;
    text-align: center;
    font-weight: 700;
    color: #374151;
  }

  .skill-body {
    flex: 1 1 auto;
    padding: 1rem;
  }

  .skill-band {
    font-size: 2.5rem;
    font-weight: 700;
    line-height: 1.1;
    text-align: center;
    color: #0056b3;
  }

  .skill-band-name {
    font-size: 0.85rem;
    font-weight: 600;
    text-align: center;
    color: #4b5563;
    margin-bottom: 0.75rem;
  }

  .skill-label {
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-size: 0.65rem;
    color: #6b7280;
    margin-top: 0.75rem;
  }

  .skill-text {
    font-size: 0.8rem;
    color: #4b5563;
    line-height: 1.6;
    margin-bottom: 0;
  }

  .skill-footer {
    padding: 0.75rem 1rem;
    border-top: 1px solid #e5e7eb;
    text-align: center;
  }

  .skill-footer:empty {
    display: none;
  }

  .comparison-table {
    width: 100%;
  }

  /* Same sheet-of-paper framing as the per-skill IELTS reports; the print
     rules in global.css strip it off .ielts-report when the report is
     printed. */
  .paper-shadow {
    position: relative;
    background: #fff;
    max-width: 1500px;
    padding: 3rem 2rem;
    margin: 50px auto;
    box-shadow: 0px 2px 38px rgba(0, 0, 0, 0.2);
  }
  .paper-shadow:after,
  .paper-shadow:before {
    content: "";
    position: absolute;
    left: auto;
    background: none;
    z-index: -1;
  }
  .paper-shadow:after {
    width: 90%;
    height: 10px;
    top: 30px;
    right: 8px;
    transform: rotate(-3deg);
    box-shadow: 0px -25px 35px 0px rgba(0, 0, 0, 0.5);
  }

  .paper-shadow:before {
    width: 10px;
    height: 95%;
    top: 5px;
    right: 18px;
    transform: rotate(3deg);
    box-shadow: 22px 0px 35px 0px rgba(0, 0, 0, 0.5);
  }

  @media (max-width: 768px) {
    .container {
      padding: 0;
    }

    .candidate-row {
      flex-direction: column;
      align-items: stretch;
    }

    .band-card {
      flex: 0 0 auto;
      width: 100%;
    }

    .band-score {
      font-size: 4rem;
    }

    .sensayLink {
      font-size: 2.5vw;
    }
  }

  @media print {
    .no-print {
      display: none !important;
    }

    /* A4 is narrower than Bootstrap's lg breakpoint, so the four skill
       columns would stack one per page. Force them back into a single row -
       the report is designed to be read four-up. */
    .skill-row {
      display: flex !important;
      flex-wrap: nowrap !important;
    }

    .skill-row > [class*="col-"] {
      flex: 1 1 0 !important;
      max-width: 25% !important;
    }

    .skill-band {
      font-size: 1.8rem;
    }

    .skill-text {
      font-size: 0.7rem;
    }

    .band-score {
      font-size: 3.5rem;
    }

    /* Bootstrap's print reboot forces every table cell to a white background.
       Showing where the candidate sits is the whole point of the comparison
       table, so the highlighted row has to win that back. */
    .comparison-table tr.table-primary > th,
    .comparison-table tr.table-primary > td {
      background-color: #cdd8f6 !important;
    }
  }
</style>
